/**
 * Shareholder structure loading for CompanyDetails.
 *
 * The structure comes from the shareholder graph view (GET_PROFILER_SHAREHOLDER_GRAPH), requested
 * at page load next to GET_PROFILER_DETAILS. Fallback order:
 *   1. graph view succeeded -> use it (card figures included; n/a when an older BE answered with
 *      the legacy flow)
 *   2. graph view failed -> the details payload's structure, when it has one (older BEs still
 *      send shareholders there); card figures n/a
 *   3. otherwise the graph status is 'failed' (the tab offers Try again for the graph call only)
 *
 * The details response carries the share of shareholder contacts (the BE reads it with the key
 * contacts), so a failed graph view leaves it in place. The key-contact ownership join
 * (applyShareholderOwnershipToContacts) only fills what an older BE leaves out; it runs for
 * whichever of the two responses arrives second and is idempotent, so running it twice is
 * harmless.
 *
 * Pure helpers over the CompanyDetails local state and the useShareholderState object, kept out of
 * the component so each stays small and testable.
 *
 * @module shareholderGraphLoading
 */
import {
  SHAREHOLDER_GRAPH_VIEW,
  applyShareholderOwnershipToContacts
} from '../../../../api/endpoints';
import { SHAREHOLDER_GRAPH_STATUS } from '../hooks/useShareholderState';

/**
 * Shareholder-structure subsidiary nodes with their ownership shares. Direct ownership is the
 * backend's share_held_by_target, the cards' 'Held' figure; a payload without that key (an older
 * backend) keeps the share of the first link into the node.
 */
export const mapSubsidiaryNodes = (shareholdersStructure) =>
  (shareholdersStructure?.nodes || [])
    .filter((entry) => entry.is_subsidiary)
    .map((node) => {
      const link = shareholdersStructure.links?.find?.((l) => l.source === node.id);
      const hasBackendShare = Object.hasOwn(node, 'share_held_by_target');
      return {
        ...node,
        direct_ownership: hasBackendShare ? node.share_held_by_target : link?.share,
        total_ownership: link?.share_total
      };
    });

const sharedProjectBody = ({ isFromFeedbackProject, project }) =>
  isFromFeedbackProject ? { sharedProjectId: project?.id } : {};

/** Body of GET_PROFILER_DETAILS; the shareholder structure comes from the graph view. */
export const buildProfilerDetailsBody = (companyId, context) => ({
  companyId,
  includeShareholders: false,
  ...sharedProjectBody(context)
});

/** Body of GET_PROFILER_SHAREHOLDER_GRAPH; sharedProjectId follows the same rule as details. */
export const buildShareholderGraphBody = (companyId, context) => ({
  companyId,
  view: SHAREHOLDER_GRAPH_VIEW,
  ...sharedProjectBody(context)
});

export const hasStructureNodes = (structure) => !!structure?.nodes?.length;

/** True until the graph structure is resolved (loaded, fallback or failed). */
export const isShareholderGraphPending = (status) =>
  status === SHAREHOLDER_GRAPH_STATUS.IDLE || status === SHAREHOLDER_GRAPH_STATUS.LOADING;

/**
 * Whether the graph view should be requested now: once per company (status idle), and only when
 * the access check is final. A Viewer's access depends on the project owner, which is known only
 * after authStore.companyUsers loaded (the details request waits for the same list).
 * @param {{status: string, canViewDetails: boolean, companyUsersLoaded: boolean}} params
 */
export const shouldRequestShareholderGraph = ({ status, canViewDetails, companyUsersLoaded }) =>
  status === SHAREHOLDER_GRAPH_STATUS.IDLE && (!!canViewDetails || !!companyUsersLoaded);

/**
 * Per-company bookkeeping of the two structure sources, and the page-state fields a resolved
 * structure writes (so a failed request for the next company never shows the previous one's).
 */
export const initialShareholderSources = () => ({
  shareholdersStructure: null,
  subsidiaries: [],
  isIndependent: false,
  // Id of the graph view request in flight for the current company, or null.
  shareholderGraphRequestId: null,
  // The graph view failed for the current company (so the details fallback may apply).
  shareholderGraphRequestFailed: false,
  // { structure, isIndependent } from the details payload of the current company, or null.
  detailsShareholders: null,
  // The details request of the current company finished (success or error).
  detailsShareholdersSettled: false
});

export const resetShareholderSources = (state) => {
  Object.assign(state, initialShareholderSources());
};

// Ids of graph view requests; a plain number, as MobX state keeps no object identity.
let lastShareholderGraphRequestId = 0;

/**
 * Starts the graph view request of the current company, unless one is in flight: a second Try
 * again joins that one. Clears the failure of an earlier request.
 * @returns {number|null} the id its answer has to carry, or null when a request is in flight
 */
export const startShareholderGraphRequest = (state) => {
  if (state.shareholderGraphRequestId !== null) {
    return null;
  }
  lastShareholderGraphRequestId += 1;
  state.shareholderGraphRequestId = lastShareholderGraphRequestId;
  state.shareholderGraphRequestFailed = false;
  return lastShareholderGraphRequestId;
};

/**
 * Whether an answer is the one of the request in flight (true once, which ends that request).
 * The answer of a request a company change (resetShareholderSources) left behind is dropped, so
 * a late failure for A can no longer mark A's newer graph as failed after A, B, A.
 */
export const settleShareholderGraphRequest = (state, requestId) => {
  if (state.shareholderGraphRequestId !== requestId) {
    return false;
  }
  state.shareholderGraphRequestId = null;
  return true;
};

/**
 * Puts a resolved structure on the page state and rebuilds the graph.
 * @param {Object} state - CompanyDetails local state
 * @param {Object} shareholderState - useShareholderState object
 * @param {{structure, isIndependent, figures, figuresUnavailable}} source
 */
export const applyShareholderStructure = (state, shareholderState, source) => {
  state.shareholdersStructure = source.structure;
  state.subsidiaries = mapSubsidiaryNodes(source.structure);
  state.isIndependent = !!source.isIndependent;
  shareholderState.setGraphFiguresUnavailable(!!source.figuresUnavailable);
  shareholderState.setGraphFigures(source.figures || null);
  shareholderState.setGraphStatus(SHAREHOLDER_GRAPH_STATUS.LOADED);
};

/**
 * Writes the graph view's ownership shares onto the key contacts already in state. The array is
 * replaced (same contact objects) so observers of state.keyContacts re-render.
 */
export const joinOwnershipIntoKeyContacts = (state, rawShareholders) => {
  if (!state.keyContacts?.length || !rawShareholders?.length) {
    return;
  }
  applyShareholderOwnershipToContacts(state.keyContacts, rawShareholders);
  state.keyContacts = state.keyContacts.slice();
};

/**
 * After the graph view failed: use the details structure once it is known, or mark the graph as
 * failed when details have none (or will not come).
 */
export const resolveShareholderFallback = (state, shareholderState) => {
  if (!state.shareholderGraphRequestFailed) {
    return;
  }
  const details = state.detailsShareholders;
  if (hasStructureNodes(details?.structure)) {
    applyShareholderStructure(state, shareholderState, {
      structure: details.structure,
      isIndependent: details.isIndependent,
      figures: null,
      figuresUnavailable: true
    });
    return;
  }
  if (state.detailsShareholdersSettled || !state.isLoadingDetails) {
    shareholderState.setGraphStatus(SHAREHOLDER_GRAPH_STATUS.FAILED);
  }
};

/**
 * Graph view success for the current company.
 * @param {Object} response - mapped payload (mapShareholderGraphPayload)
 * @param {{current: Object[]|null}} rowsRef - keeps the raw rows for a later details response
 */
export const onShareholderGraphLoaded = (state, shareholderState, response, rowsRef) => {
  rowsRef.current = response?.shareholders || [];
  joinOwnershipIntoKeyContacts(state, rowsRef.current);
  applyShareholderStructure(state, shareholderState, {
    structure: response?.shareholdersStructure || { nodes: [], links: [] },
    isIndependent: response?.isIndependent,
    figures: response?.figuresByMemberId,
    figuresUnavailable: !!response?.isLegacyPayload
  });
};

/** No access to the shareholder structure: settle as an empty structure (no endless loader). */
export const onShareholderGraphAccessDenied = (state, shareholderState) => {
  applyShareholderStructure(state, shareholderState, {
    structure: null,
    isIndependent: false,
    figures: null,
    figuresUnavailable: true
  });
};

export const onShareholderGraphFailed = (state, shareholderState) => {
  state.shareholderGraphRequestFailed = true;
  resolveShareholderFallback(state, shareholderState);
};

/**
 * Details success: the key contacts get the graph view's ownership shares when the graph
 * arrived first; the details structure is kept for the fallback only.
 * @param {Object} response - mapped GET_PROFILER_DETAILS payload (keyContacts mutated in place)
 */
export const onDetailsShareholdersLoaded = (state, shareholderState, response, rowsRef) => {
  applyShareholderOwnershipToContacts(response?.keyContacts, rowsRef.current);
  state.detailsShareholders = {
    structure: response?.shareholdersStructure || null,
    isIndependent: !!response?.isIndependent
  };
  // The target card's location may fall back to the company details that just arrived.
  shareholderState.refreshTargetLocation();
};

export const onDetailsShareholdersSettled = (state, shareholderState) => {
  state.detailsShareholdersSettled = true;
  resolveShareholderFallback(state, shareholderState);
};
